import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, map, switchMap, take} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ArtifactId, ExpeditionId, FactSessionId, FindingId} from '@webmate-web-core/shared/types/ids';
import {
    FactFindingInfo,
    FactInfo,
    isFactSessionFactStreamReady,
    isFactSessionFactsAdded,
    isFindingFactId,
    isFindingFactInfo
} from '@webmate-web-core/shared/types/fact';
import {Finding, FindingSeverity} from '@webmate-web-core/shared/types/testmgmt';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {FactService} from '@webmate-web-core/shared/services/fact.service';
import {TestApi} from '@webmate-web-core/shared/api/rest/test.restapi';
import {WorkbenchContextService} from './workbench-context.service';
import {WORKBENCH_QUALITY_SENSE_POLICY} from '../quality-sense';

export type WorkbenchQualitySenseStatus =
    'idle' |
    'starting' |
    'monitoring' |
    'monitoring-disconnected' |
    'not-monitored' |
    'load-failed' |
    'ended';

export type QualitySenseObservationDetailStatus = 'not-loaded' | 'loading' | 'loaded' | 'load-failed';

export interface QualitySenseHttpResponseDetails {
    requestId?: string;
    url?: string;
    status?: number;
    statusText?: string;
    requestMethod?: string;
    resourceType?: string;
    fromDiskCache?: boolean;
    fromServiceWorker?: boolean;
}

export interface QualitySenseConsoleMessageDetails {
    source?: string;
    level?: string;
    text?: string;
    url?: string;
    line?: number;
    column?: number;
}

export interface QualitySenseObservationDetails {
    signal?: string;
    predicate?: Record<string, unknown>;
    response?: QualitySenseHttpResponseDetails;
    message?: QualitySenseConsoleMessageDetails;
}

export interface QualitySenseObservation {
    findingId: FindingId;
    version: number;
    creationTime: DateTime;
    title?: string;
    findingType: string;
    severity: FindingSeverity;
    associatedArtifacts: ArtifactId[];
    detailStatus: QualitySenseObservationDetailStatus;
    details?: QualitySenseObservationDetails;
    acked: boolean;
    unread: boolean;
}

export interface WorkbenchQualitySenseState {
    status: WorkbenchQualitySenseStatus;
    expeditionId?: ExpeditionId;
    factSessionId?: FactSessionId;
    observations: readonly QualitySenseObservation[];
    unacknowledgedCount: number;
    unreadCount: number;
    panelOpen: boolean;
    errorMessage?: string;
}

interface QualitySenseSessionState {
    expeditionId: ExpeditionId;
    factSessionId: FactSessionId;
    status: WorkbenchQualitySenseStatus;
    observations: Map<FindingId, QualitySenseObservation>;
    errorMessage?: string;
}

const monitoredFindingTypes = new Set<string>(
    WORKBENCH_QUALITY_SENSE_POLICY.requestedFacts.map(request => request.factType.findingType)
);

const initialState = (panelOpen: boolean): WorkbenchQualitySenseState => ({
    status: 'idle',
    observations: [],
    unacknowledgedCount: 0,
    unreadCount: 0,
    panelOpen
});

@UntilDestroy()
@Injectable()
export class WorkbenchQualitySenseService implements OnDestroy {
    private readonly sessions = new Map<FactSessionId, QualitySenseSessionState>();
    private readonly stateSubject = new BehaviorSubject<WorkbenchQualitySenseState>(initialState(false));
    private readonly observationArrivalsSubject = new Subject<number>();
    private activeExpedition?: BrowserSessionInfo;
    private activeFactSessionId?: FactSessionId;
    private monitoringSubscription?: Subscription;
    private readonly detailSubscriptions = new Subscription();
    private panelOpen = false;

    readonly activeState$: Observable<WorkbenchQualitySenseState> = this.stateSubject.asObservable();
    readonly observationArrivals$: Observable<number> = this.observationArrivalsSubject.asObservable();

    constructor(private readonly workbenchContextService: WorkbenchContextService,
                private readonly factService: FactService,
                private readonly testApi: TestApi) {
        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            map(context => context.expeditionContext?.activeExpedition),
            distinctUntilChanged((previous, current) =>
                previous?.id === current?.id &&
                previous?.startupFactSessionId === current?.startupFactSessionId &&
                previous?.sessionState === current?.sessionState &&
                previous?.optEnd === current?.optEnd
            ),
            untilDestroyed(this)
        ).subscribe(expedition => this.activateExpedition(expedition));
    }

    setPanelOpen(open: boolean): void {
        if (this.panelOpen === open) {
            return;
        }

        this.panelOpen = open;
        const session = this.getActiveSession();
        if (session) {
            this.updateObservationFlags(session, observation => open
                ? {...observation, acked: true}
                : {...observation, unread: false});
        }
        this.publishActiveState();
    }

    markAllSeen(): void {
        const session = this.getActiveSession();
        if (!session) {
            return;
        }
        this.updateObservationFlags(session, observation => ({...observation, acked: true, unread: false}));
        this.publishActiveState();
    }

    markObservationSeen(findingId: FindingId): void {
        const session = this.getActiveSession();
        const observation = session?.observations.get(findingId);
        if (!session || !observation || (observation.acked && !observation.unread)) {
            return;
        }

        session.observations.set(findingId, {...observation, acked: true, unread: false});
        this.publishActiveState();
    }

    loadObservationDetails(findingId: FindingId): void {
        const session = this.getActiveSession();
        if (!session) {
            return;
        }
        this.requestObservationDetails(session, findingId);
    }

    private requestObservationDetails(session: QualitySenseSessionState, findingId: FindingId): void {
        const observation = session.observations.get(findingId);
        if (!observation || ['loading', 'loaded'].includes(observation.detailStatus)) {
            return;
        }
        const requestedVersion = observation.version;

        session.observations.set(findingId, {...observation, detailStatus: 'loading'});
        if (this.activeFactSessionId === session.factSessionId) {
            this.publishActiveState();
        }

        this.detailSubscriptions.add(this.testApi.getFinding$(findingId).pipe(take(1)).subscribe({
            next: finding => {
                const current = session.observations.get(findingId);
                if (!current || current.version !== requestedVersion) {
                    return;
                }

                if (finding.id !== findingId || finding.version < requestedVersion) {
                    session.observations.set(findingId, {...current, detailStatus: 'load-failed'});
                } else if (finding.version > requestedVersion) {
                    this.addObservation(session, this.fromFinding(finding));
                } else {
                    session.observations.set(findingId, {
                        ...current,
                        detailStatus: 'loaded',
                        details: detailsFromProperties(finding.properties)
                    });
                }
                if (this.activeFactSessionId === session.factSessionId) {
                    this.publishActiveState();
                }
            },
            error: error => {
                const current = session.observations.get(findingId);
                if (!current || current.version !== requestedVersion) {
                    return;
                }
                console.warn(`Could not load QualitySense observation ${findingId}.`, error);
                session.observations.set(findingId, {...current, detailStatus: 'load-failed'});
                if (this.activeFactSessionId === session.factSessionId) {
                    this.publishActiveState();
                }
            }
        }));
    }

    retryLoad(): void {
        const session = this.getActiveSession();
        if (!session) {
            return;
        }
        this.startMonitoring(session);
    }

    ngOnDestroy(): void {
        this.monitoringSubscription?.unsubscribe();
        this.detailSubscriptions.unsubscribe();
    }

    private activateExpedition(expedition?: BrowserSessionInfo): void {
        const previousSession = this.getActiveSession();
        const nextFactSessionId = expedition?.startupFactSessionId;
        const monitoringTargetChanged = this.activeExpedition?.id !== expedition?.id ||
            this.activeFactSessionId !== nextFactSessionId;

        if (monitoringTargetChanged && previousSession && this.panelOpen) {
            this.updateObservationFlags(previousSession, observation => ({...observation, unread: false}));
        }

        this.activeExpedition = expedition;
        this.activeFactSessionId = nextFactSessionId;

        if (!expedition) {
            this.stopMonitoring();
            this.stateSubject.next(initialState(this.panelOpen));
            return;
        }

        if (this.isEnded(expedition)) {
            this.stopMonitoring();
            const session = nextFactSessionId ? this.getOrCreateSession(expedition, nextFactSessionId) : undefined;
            if (session) {
                session.status = 'ended';
                session.errorMessage = undefined;
                this.updateObservationFlags(session, observation => ({...observation, acked: true, unread: false}));
            }
            this.publishActiveState('ended');
            return;
        }

        if (!nextFactSessionId) {
            this.stopMonitoring();
            this.publishActiveState(this.statusWithoutFactSession());
            return;
        }

        const session = this.getOrCreateSession(expedition, nextFactSessionId);
        if (this.panelOpen) {
            this.updateObservationFlags(session, observation => ({...observation, acked: true}));
        }

        if (monitoringTargetChanged || !this.monitoringSubscription) {
            this.startMonitoring(session);
        } else {
            this.publishActiveState();
        }
    }

    private startMonitoring(session: QualitySenseSessionState): void {
        this.stopMonitoring();
        this.activeFactSessionId = session.factSessionId;
        session.status = 'starting';
        session.errorMessage = undefined;
        this.publishActiveState();

        const subscription = new Subscription();
        let snapshotRequested = false;
        subscription.add(this.factService.observeFactSessionFacts$(session.factSessionId).subscribe({
            next: message => {
                if (message.factSessionId !== session.factSessionId || this.activeFactSessionId !== session.factSessionId) {
                    return;
                }

                if (isFactSessionFactsAdded(message)) {
                    this.addLiveFacts(session, message.facts);
                } else if (isFactSessionFactStreamReady(message) && !snapshotRequested) {
                    snapshotRequested = true;
                    subscription.add(this.loadSnapshot(session));
                }
            },
            error: error => this.markStreamDisconnected(session, error),
            complete: () => this.markStreamDisconnected(session)
        }));
        this.monitoringSubscription = subscription;
    }

    private loadSnapshot(session: QualitySenseSessionState): Subscription {
        return this.factService.getFactSessionInfo$(session.factSessionId).pipe(
            map(info => Array.from(new Set(info.facts.filter(isFindingFactId).map(fact => fact.findingId)))),
            switchMap(findingIds => findingIds.length > 0 ? this.testApi.getFindings$(findingIds) : of([] as Finding[])),
            take(1)
        ).subscribe({
            next: findings => {
                if (this.activeFactSessionId !== session.factSessionId) {
                    return;
                }
                let newObservations = 0;
                findings.forEach(finding => {
                    const wasKnown = session.observations.has(finding.id);
                    const changed = this.addObservation(session, this.fromFinding(finding));
                    if (changed && !wasKnown) {
                        newObservations++;
                    }
                });
                if (session.status !== 'monitoring-disconnected') {
                    session.status = 'monitoring';
                    session.errorMessage = undefined;
                }
                this.publishActiveState();
                if (newObservations > 0) {
                    this.observationArrivalsSubject.next(newObservations);
                }
            },
            error: error => {
                if (session.status === 'monitoring-disconnected') {
                    console.warn(`Could not load QualitySense FactSession ${session.factSessionId}.`, error);
                } else {
                    this.markLoadFailed(session, error);
                }
            }
        });
    }

    private addLiveFacts(session: QualitySenseSessionState, facts: FactInfo[]): void {
        let changed = false;
        let newObservations = 0;
        facts.filter(isFindingFactInfo).forEach(fact => {
            const finding = fact.artifactOrFinding.finding;
            const wasKnown = session.observations.has(finding.id);
            const observationChanged = this.addObservation(session, this.fromFactFindingInfo(finding));
            changed = observationChanged || changed;
            if (observationChanged && !wasKnown) {
                newObservations++;
            }
        });
        if (changed) {
            this.publishActiveState();
        }
        if (newObservations > 0) {
            this.observationArrivalsSubject.next(newObservations);
        }
    }

    private addObservation(session: QualitySenseSessionState, observation: QualitySenseObservation): boolean {
        if (!monitoredFindingTypes.has(observation.findingType)) {
            return false;
        }

        const existing = session.observations.get(observation.findingId);
        if (existing && existing.version > observation.version) {
            return false;
        }

        if (existing) {
            const incomingHasDetails = observation.detailStatus === 'loaded';
            const sameVersion = existing.version === observation.version;
            const refreshRequestedDetails = !sameVersion && !incomingHasDetails && existing.detailStatus !== 'not-loaded';
            session.observations.set(observation.findingId, {
                ...observation,
                detailStatus: incomingHasDetails
                    ? 'loaded'
                    : sameVersion ? existing.detailStatus : 'not-loaded',
                details: incomingHasDetails
                    ? observation.details
                    : sameVersion ? existing.details : undefined,
                acked: existing.acked,
                unread: existing.unread
            });
            if (refreshRequestedDetails) {
                this.requestObservationDetails(session, observation.findingId);
            }
        } else {
            session.observations.set(observation.findingId, {
                ...observation,
                acked: this.panelOpen,
                unread: true
            });
        }
        return !existing || existing.version !== observation.version;
    }

    private fromFactFindingInfo(finding: FactFindingInfo): QualitySenseObservation {
        return {
            findingId: finding.id,
            version: finding.version,
            creationTime: finding.creationTime,
            title: finding.title ?? undefined,
            findingType: finding.resultType,
            severity: finding.severity,
            associatedArtifacts: finding.associatedArtifacts,
            detailStatus: 'not-loaded',
            acked: false,
            unread: true
        };
    }

    private fromFinding(finding: Finding): QualitySenseObservation {
        return {
            findingId: finding.id,
            version: finding.version,
            creationTime: finding.creationTime,
            title: finding.title,
            findingType: finding.issueType,
            severity: finding.severity,
            associatedArtifacts: finding.associatedArtifacts,
            detailStatus: 'loaded',
            details: detailsFromProperties(finding.properties),
            acked: false,
            unread: true
        };
    }

    private markLoadFailed(session: QualitySenseSessionState, error: unknown): void {
        if (this.activeFactSessionId !== session.factSessionId) {
            return;
        }
        console.warn(`Could not load QualitySense FactSession ${session.factSessionId}.`, error);
        session.status = 'load-failed';
        session.errorMessage = 'Could not load QualitySense observations.';
        this.publishActiveState();
    }

    private markStreamDisconnected(session: QualitySenseSessionState, error?: unknown): void {
        if (this.activeFactSessionId !== session.factSessionId) {
            return;
        }
        if (error === undefined) {
            console.warn(`QualitySense FactSession stream ${session.factSessionId} disconnected.`);
        } else {
            console.warn(`QualitySense FactSession stream ${session.factSessionId} disconnected.`, error);
        }
        session.status = 'monitoring-disconnected';
        session.errorMessage = 'QualitySense monitoring disconnected. New observations may be missed.';
        this.publishActiveState();
    }

    private getOrCreateSession(expedition: BrowserSessionInfo, factSessionId: FactSessionId): QualitySenseSessionState {
        const existing = this.sessions.get(factSessionId);
        if (existing) {
            existing.expeditionId = expedition.id;
            return existing;
        }

        const session: QualitySenseSessionState = {
            expeditionId: expedition.id,
            factSessionId,
            status: 'starting',
            observations: new Map<FindingId, QualitySenseObservation>()
        };
        this.sessions.set(factSessionId, session);
        return session;
    }

    private getActiveSession(): QualitySenseSessionState | undefined {
        return this.activeFactSessionId ? this.sessions.get(this.activeFactSessionId) : undefined;
    }

    private stopMonitoring(): void {
        this.monitoringSubscription?.unsubscribe();
        this.monitoringSubscription = undefined;
    }

    private updateObservationFlags(
        session: QualitySenseSessionState,
        update: (observation: QualitySenseObservation) => QualitySenseObservation
    ): void {
        session.observations.forEach((observation, findingId) => {
            session.observations.set(findingId, update(observation));
        });
    }

    private publishActiveState(statusOverride?: WorkbenchQualitySenseStatus): void {
        const session = this.getActiveSession();
        if (!session) {
            this.stateSubject.next({
                ...initialState(this.panelOpen),
                status: statusOverride ?? this.statusWithoutFactSession(),
                expeditionId: this.activeExpedition?.id
            });
            return;
        }

        const observations = Array.from(session.observations.values()).sort((left, right) =>
            new Date(right.creationTime).getTime() - new Date(left.creationTime).getTime()
        );
        this.stateSubject.next({
            status: statusOverride ?? session.status,
            expeditionId: session.expeditionId,
            factSessionId: session.factSessionId,
            observations,
            unacknowledgedCount: observations.filter(observation => !observation.acked).length,
            unreadCount: observations.filter(observation => observation.unread).length,
            panelOpen: this.panelOpen,
            errorMessage: session.errorMessage
        });
    }

    private isEnded(expedition: BrowserSessionInfo): boolean {
        return !!expedition.optEnd || ['finished', 'failed', 'stopped', 'ended'].includes(expedition.sessionState.toLowerCase());
    }

    private statusWithoutFactSession(): WorkbenchQualitySenseStatus {
        if (!this.activeExpedition) {
            return 'idle';
        }
        if (this.isEnded(this.activeExpedition)) {
            return 'ended';
        }
        return ['starting', 'leased'].includes(this.activeExpedition.sessionState.toLowerCase())
            ? 'starting'
            : 'not-monitored';
    }
}

function detailsFromProperties(properties: unknown): QualitySenseObservationDetails {
    const source = asRecord(properties);
    if (!source) {
        return {};
    }

    const response = asRecord(source.response);
    const message = asRecord(source.message);
    return {
        signal: asString(source.signal),
        predicate: asRecord(source.predicate),
        response: response ? {
            requestId: asString(response.requestId),
            url: asString(response.url),
            status: asNumber(response.status),
            statusText: asString(response.statusText),
            requestMethod: asString(response.requestMethod),
            resourceType: asString(response.resourceType),
            fromDiskCache: asBoolean(response.fromDiskCache),
            fromServiceWorker: asBoolean(response.fromServiceWorker)
        } : undefined,
        message: message ? {
            source: asString(message.source),
            level: asString(message.level),
            text: asString(message.text),
            url: asString(message.url),
            line: asNumber(message.line),
            column: asNumber(message.column)
        } : undefined
    };
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value as Record<string, unknown>
        : undefined;
}

function asString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
    return typeof value === 'number' ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
    return typeof value === 'boolean' ? value : undefined;
}
